import { AirQualityCardData } from "modules/assets/AirQualityCard";
import { ThermalCardData } from "modules/assets/ThermalCard";
import { TagTone } from "modules/assets/Tag";
import { StatCardData } from "modules/common/StatCard";

export const STAT_CARDS: StatCardData[] = [
  {
    title: "Compliance score",
    value: "100%",
    description: "High compliance",
    icon: "shield",
    tone: "green",
    tooltip: "Per DM-HSD-GU119.",
  },
  {
    title: "Compl. improvement",
    value: "+10%",
    description: "Vs previous 30 days",
    icon: "course-up",
    tone: "green",
    tooltip: "This status represents the overall condition of the zone and is based on the most critical metric currently detected",
  },
  { title: "Avg incidents/month", value: "4", description: "Last 3 months", icon: "danger", tone: "red" },
  { title: "Total active incidents", value: "4", description: "Open, all severities", icon: "danger", tone: "red" },
];

export type LegendItem = { label: string; tag: { tone: TagTone; label: string } };

export const LEGEND: LegendItem[] = [
  { label: "Instant ceiling — never exceed even momentarily", tag: { tone: "violet", label: "TLV-C" } },
  { label: "Short-term average (30-min/1-hour/8-hour)", tag: { tone: "blue", label: "ST" } },
  { label: "Long-term average (8-hour/24-hour)", tag: { tone: "green", label: "LT" } },
];

const FOOTER_NOTE = "Click to see per-sensor breakdown";

export const AIR_QUALITY_CARDS: AirQualityCardData[] = [
  {
    formula: "CO₂",
    unit: "ppm",
    name: "Carbon dioxide",
    status: { tone: "orange", label: "Warning" },
    realtimeLabel: "Real-time",
    realtimeValue: "870",
    realtimeUnit: "ppm",
    complianceLabel: "Limit compliance",
    metrics: [
      {
        tone: "green",
        value: "870 ppm",
        badge: { tone: "green", label: "Good" },
        label: "Instant ceiling",
        tag: { tone: "violet", label: "TLV-C" },
        progress: 45,
        limit: "Limit: 5000 ppm",
      },
      {
        tone: "green",
        value: "+870 ppm",
        badge: { tone: "green", label: "Good" },
        label: "Short-term - vs outdoor level",
        tag: { tone: "blue", label: "ST" },
        subInputs: [
          { label: "Outdoor", value: "420", unit: "ppm" },
          { label: "Indoor", value: "870", unit: "ppm" },
        ],
        delta: { label: "Delta (above outdoor)", value: "+870", unit: "ppm" },
        progress: 90,
        limit: "Limit +700 ppm above outdoor",
      },
      {
        tone: "orange",
        value: "795 ppm",
        badge: { tone: "orange", label: "Warning" },
        label: "Long-term: 8-hour average",
        tag: { tone: "green", label: "LT" },
        progress: 99,
        limit: "Limit: 800 ppm",
      },
    ],
    footerNote: FOOTER_NOTE,
    updatedNote: "Last update: 2 min ago",
  },
  {
    formula: "HCHO",
    unit: "mg/m³",
    name: "Formaldehyde",
    status: { tone: "green", label: "Good" },
    realtimeLabel: "Real-time",
    realtimeValue: "0.009",
    realtimeUnit: "mg/m³",
    complianceLabel: "Limit compliance",
    metrics: [
      {
        tone: "green",
        value: "0.009 mg/m³",
        badge: { tone: "green", label: "Good" },
        label: "Instant ceiling",
        tag: { tone: "violet", label: "TLV-C" },
        limit: "Limit: 1 mg/m³",
      },
      {
        tone: "green",
        value: "0.010 mg/m³",
        badge: { tone: "green", label: "Good" },
        label: "Short-term: 30-min average",
        tag: { tone: "blue", label: "ST" },
        limit: "Limit: 0.08 mg/m³",
      },
      {
        tone: "green",
        value: "0.008 mg/m³",
        badge: { tone: "green", label: "Good" },
        label: "Long-term: 8-hour average",
        tag: { tone: "green", label: "LT" },
        limit: "Limit: 0.01 mg/m³",
      },
    ],
    footerNote: FOOTER_NOTE,
    updatedNote: "Last update: 2 min ago",
  },
  {
    formula: "TVOC",
    unit: "µg/m³",
    name: "Total volatile organic compounds",
    status: { tone: "green", label: "Good" },
    realtimeLabel: "Real-time",
    realtimeValue: "0.009",
    realtimeUnit: "µg/m³",
    complianceLabel: "Limit compliance",
    metrics: [
      {
        tone: "green",
        value: "0.42 µg/m³",
        badge: { tone: "green", label: "Good" },
        label: "Instant ceiling",
        tag: { tone: "violet", label: "TLV-C" },
        progress: 8,
        limit: "Limit: 5.5 µg/m³",
      },
      {
        tone: "green",
        value: "0.010 µg/m³",
        badge: { tone: "green", label: "Good" },
        label: "Short-term: 30-min average",
        tag: { tone: "blue", label: "ST" },
        progress: 4,
        limit: "Limit: 3 µg/m³",
      },
      {
        tone: "green",
        value: "0.038 µg/m³",
        badge: { tone: "green", label: "Good" },
        label: "Long-term: 8-hour average",
        tag: { tone: "green", label: "LT" },
        progress: 6,
        limit: "Limit: 0.6 µg/m³",
      },
    ],
    footerNote: FOOTER_NOTE,
    updatedNote: "Last update: 2 min ago",
  },
  {
    formula: "PM2.5",
    unit: "µg/m³",
    name: "Particle Pollution 2.5",
    status: { tone: "red", label: "Alert" },
    realtimeLabel: "Real-time",
    realtimeValue: "28",
    realtimeUnit: "µg/m³",
    complianceLabel: "Limit compliance",
    metrics: [
      {
        tone: "gray",
        value: "28 µg/m³",
        label: "Instant ceiling",
        tag: { tone: "violet", label: "TLV-C" },
        limit: "No instant TLV-C per DM-HSD-GU119",
      },
      {
        tone: "green",
        value: "32 µg/m³",
        badge: { tone: "green", label: "Good" },
        label: "Short-term: 1-hour average",
        tag: { tone: "blue", label: "ST" },
        progress: 32,
        limit: "Limit: 100 µg/m³",
      },
      {
        tone: "red",
        value: "41 µg/m³",
        badge: { tone: "red", label: "Alert" },
        label: "Long-term: 24-hour average",
        tag: { tone: "green", label: "LT" },
        progress: 100,
        limit: "Limit: 35 µg/m³",
      },
    ],
    footerNote: FOOTER_NOTE,
    updatedNote: "Last update: 2 min ago",
  },
  {
    formula: "PM10",
    unit: "µg/m³",
    name: "Particle Pollution 10",
    status: { tone: "green", label: "Good" },
    realtimeLabel: "Real-time",
    realtimeValue: "55",
    realtimeUnit: "µg/m³",
    complianceLabel: "Limit compliance",
    metrics: [
      {
        tone: "gray",
        value: "28 µg/m³",
        label: "Instant ceiling",
        tag: { tone: "violet", label: "TLV-C" },
        limit: "No instant TLV-C per DM-HSD-GU119",
      },
      {
        tone: "green",
        value: "68 µg/m³",
        badge: { tone: "green", label: "Good" },
        label: "Short-term: 8-hour average",
        tag: { tone: "blue", label: "ST" },
        progress: 68,
        limit: "Limit: 100 µg/m³",
      },
      {
        tone: "green",
        value: "72 µg/m³",
        badge: { tone: "green", label: "Good" },
        label: "Long-term: 24-hour average",
        tag: { tone: "green", label: "LT" },
        progress: 48,
        limit: "Limit: 150 µg/m³",
      },
    ],
    footerNote: FOOTER_NOTE,
    updatedNote: "Last update: 2 min ago",
  },
];

export const THERMAL_CARDS: ThermalCardData[] = [
  {
    title: "Air temperature",
    unit: "°C",
    status: { tone: "orange", label: "Warning" },
    realtimeLabel: "Real-time",
    realtimeValue: "27",
    realtimeUnit: "°C",
    gauge: { min: 18, max: 30, rangeLow: 22.5, rangeHigh: 25.5, value: 27, tone: "orange" },
    rangeCaption: "Recommended range: 22.5 - 25.5 °C",
    footerNote: FOOTER_NOTE,
    updatedNote: "Last update: 2 min ago",
  },
  {
    title: "Relative humidity",
    unit: "%",
    status: { tone: "red", label: "Alert" },
    realtimeLabel: "Real-time",
    realtimeValue: "27",
    realtimeUnit: "%",
    gauge: { min: 18, max: 30, rangeLow: 22.5, rangeHigh: 25.5, value: 27, tone: "red" },
    rangeCaption: "Recommended range: 22.5 - 25.5 %",
    alert: {
      tone: "red",
      title: "Temperature-adjusted humidity limit",
      value: "58 %",
      lines: [
        "At current 27.2°C → max allowed: 55.1%",
        "Interpolated from: 56%RH at 27°C ↔ 86%RH at 19°C",
      ],
      emphasis: "Exceeds limit: max 55,3% at 27,2 °C — Compliance Alert",
    },
    footerNote: FOOTER_NOTE,
    updatedNote: "Last update: never",
  },
];
